/**
 * Report Builder Form - Unified Component
 * Handles report generation from templates with pre-filled questions
 */

import React, { useState } from 'react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { ReportTemplate } from '../../types/reports';
import { FileText, Edit2 } from 'lucide-react';

interface ReportBuilderFormProps {
  template: ReportTemplate;
  onGenerate: (clientName: string, questions: any[]) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ReportBuilderForm({ template, onGenerate, onCancel, isLoading = false }: ReportBuilderFormProps) {
  const [clientName, setClientName] = useState('');
  const [customizeQuestions, setCustomizeQuestions] = useState(false);
  const [questions, setQuestions] = useState(template.questions || []);

  const handleSubmit = () => {
    if (!clientName.trim()) {
      alert('Please enter a client name');
      return;
    }
    
    const finalClientName = clientName || `${template.name} - ${new Date().toLocaleDateString()}`;
    console.log('[ReportBuilderForm] Submitting report:', {
      type: template.type,
      client_name: finalClientName,
      questions: questions.length,
    });
    onGenerate(finalClientName, questions);
  };

  const handleQuestionChange = (index: number, field: 'section' | 'question', value: string) => {
    const newQuestions = [...questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setQuestions(newQuestions);
  };

  const handleAddQuestion = () => {
    setQuestions([...questions, { section: '', question: '' }]);
  };

  const handleRemoveQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <div className="space-y-6">
          {/* Template Info */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${template.color} flex items-center justify-center flex-shrink-0`}>
                <template.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{template.name}</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">{template.description}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Frequency:</span>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{template.regulatoryContext.frequency}</div>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Deadline:</span>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{template.regulatoryContext.deadline}</div>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Questions:</span>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{questions.length}</div>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Authorities:</span>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{template.regulatoryContext.authorities.length}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Client Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Client Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name for this report"
              className="w-full"
            />
          </div>

          {/* Questions Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Report Questions ({questions.length})
              </label>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCustomizeQuestions(!customizeQuestions)}
              >
                <Edit2 className="h-4 w-4 mr-2" />
                {customizeQuestions ? 'Use Default' : 'Customize Questions'}
              </Button>
            </div>

            {!customizeQuestions ? (
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-96 overflow-y-auto">
                <div className="space-y-3">
                  {questions.map((q, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{q.section}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">{q.question}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {questions.map((q, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium flex-shrink-0 mt-2">
                      {idx + 1}
                    </div>
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Section"
                        value={q.section}
                        onChange={(e) => handleQuestionChange(idx, 'section', e.target.value)}
                        className="w-full"
                      />
                      <Input
                        placeholder="Question"
                        value={q.question}
                        onChange={(e) => handleQuestionChange(idx, 'question', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveQuestion(idx)}
                      className="mt-2"
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAddQuestion}
                  className="w-full"
                >
                  + Add Question
                </Button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!clientName.trim() || questions.length === 0 || isLoading}
              isLoading={isLoading}
              className="flex-1"
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}











